'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import type { Mentor } from '@/types/mentor';
import { useSessionStore } from '@/stores/useSessionStore';

/* ------------------ Types ------------------ */
type User = {
  id: string;
  name: string | null;
  email: string;
  role: string;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  pendingEmail?: string | null;
  location?: string | null;
  timezone?: string;
  mentor?: Mentor | null;
  isMentorApproved: boolean;
  avatarId: string | null;
};

type Ctx = {
  user: User | null;
  mentor: Mentor | null;
  userLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

/* ------------------ Context ------------------ */
const CurrentUserContext = createContext<Ctx>({
  user: null,
  mentor: null,
  userLoading: true,
  error: null,
  refresh: async () => {},
});

/* ------------------ Provider ------------------ */
export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
  const { session, sessionLoading } = useSessionStore();

  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCurrentUser = useCallback(async () => {
    try {
      setUserLoading(true);
      setError(null);
      const res = await fetch('/api/auth/current-user', { credentials: 'include' });
      const data = await res.json();

      if (res.ok) {
        setUser(data);
      } else {
        throw new Error(data.error || 'Failed to load user');
      }
    } catch (err: any) {
      console.error('Error fetching current user:', err);
      setError(err.message || 'Failed to load user');
      setUser(null);
    } finally {
      setUserLoading(false);
    }
  }, []);

  /* Re-fetch whenever the *session user* changes (including sign-out) */
  const prevSig = useRef<string | null>(null);
  useEffect(() => {
    if (sessionLoading) return; // wait until session store finished

    // stringify user (or use a constant) to detect meaningful changes
    const sig = session?.user ? JSON.stringify(session.user) : 'guest';

    if (sig === prevSig.current) return; // nothing new
    prevSig.current = sig;

    // Signed out → clear state and stop loading
    if (!session?.user?.id) {
      setUser(null);
      setUserLoading(false);
      return;
    }

    // Signed in or user object changed → fetch fresh data
    fetchCurrentUser();
  }, [sessionLoading, session, fetchCurrentUser]);

  return (
    <CurrentUserContext.Provider
      value={{
        user,
        mentor: user?.mentor ?? null,
        userLoading,
        error,
        refresh: fetchCurrentUser,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
}

/* ------------------ Hooks ------------------ */
export function useCurrentUser() {
  return useContext(CurrentUserContext);
}

/** Convenience wrapper for legacy code */
export function useCurrentMentor() {
  const { mentor, userLoading, error, refresh } = useContext(CurrentUserContext);
  return { mentor, userLoading, error, refresh };
}
